import { useNavigate } from "react-router-dom";
import { useCallback, useEffect, useState } from "react";
import { motion } from "framer-motion";
import { Lock, Mail, User, UserPlus } from "lucide-react";

import { useAuth } from "../context/AuthContext";
import { useToast } from "../hooks/use-toast";
import { useAuthOverlay } from "./AuthOverlay";
import { useCommon } from "../context/CommonContext";
import AuthShell from "./AuthShell";
import { getAuthFlags } from "./authHelpers";

import { Button } from "../components/ui/button";
import AuthFormField from "../components/layout/AuthFormField";
import TurnstilWidget from "../components/layout/TurnstilWidget";
import { BASE_URL } from "../lib/utils";

export const SignUpPage = () => {
	const { open } = useAuthOverlay();
	const { settings } = useCommon();

	const { register, loginWithGoogleToken, user, isLoading, error } = useAuth();
	const { toast } = useToast();
	const navigate = useNavigate();

	const [email, setEmail] = useState("");
	const [password, setPassword] = useState("");
	const [displayName, setDisplayName] = useState("");
	const [captchaVerified, setCaptchaVerified] = useState(false);
	const [rememberMe, setRememberMe] = useState(false);
	const [showEmailForm, setShowEmailForm] = useState(false);

	const {
		allowEmailPassword,
		allowGitLogin,
		allowGoogleLogin,
		allowLinkedInLogin,
	} = getAuthFlags(settings);

	const allDisabled = !allowEmailPassword && !allowGitLogin && !allowGoogleLogin && !allowLinkedInLogin;

	useEffect(() => {
		const stored = localStorage.getItem("remember-me") === "true";
		setRememberMe(stored);
	}, []);

	useEffect(() => {
		if (user) {
			navigate("/", { replace: true });
		}
	}, [user, navigate]);

	const handleChangeRememberMe = useCallback((event) => {
		setRememberMe(event.target.checked);
		localStorage.setItem("remember-me", event.target.checked);
	}, []);

	const handleSubmit = useCallback(
		(event) => {
			event.preventDefault();

			if (!allowEmailPassword) return;

			if (!email || !password || !displayName) {
				toast({
					title: "Missing fields",
					description: "Please fill in all required fields.",
					variant: "destructive",
				});
				return;
			}

			if (!captchaVerified) {
				toast({
					title: "CAPTCHA required",
					description: "Please verify CAPTCHA.",
					variant: "destructive",
				});
				return;
			}

			register({
				email,
				password,
				name: displayName,
			});
		},
		[allowEmailPassword, captchaVerified, displayName, email, password, register, toast]
	);

	const submitButtonDisabled =
		isLoading || (allowEmailPassword && (!email || !password || !displayName || !captchaVerified));

	return (
		<AuthShell
			title="Create Account"
			description={
				<>
					Start building and publishing games on
					<span className="ml-1 font-medium text-foreground">CoffeeGamesOnline</span>
				</>
			}
			footerPrompt="Already have an account?"
			footerActionLabel="Log in"
			onFooterAction={() => open("login")}
			toggleDescription="Create account using email"
			toggleIcon={<Mail className="h-5 w-5 text-primary" />}
			showEmailForm={showEmailForm}
			onToggleEmailForm={setShowEmailForm}
			allowEmailPassword={allowEmailPassword}
			allDisabled={allDisabled}
			error={error}
			emailDisabledMessage="Email / Password registration is disabled."
			allowGit={allowGitLogin}
			allowGoogle={allowGoogleLogin}
			allowLinkedIn={allowLinkedInLogin}
			onGithub={() => allowGitLogin && (window.location.href = `${BASE_URL}/auth/github`)}
			onGoogle={(token) => allowGoogleLogin && loginWithGoogleToken?.(token)}
			isLoading={isLoading}
			preFormContent={
				null
			}
		>
			<motion.form
				initial={{ opacity: 0, height: 0 }}
				animate={{ opacity: 1, height: "auto" }}
				onSubmit={handleSubmit}
				className="space-y-5"
			>
				<AuthFormField
					label="Display Name"
					type="text"
					value={displayName}
					onChange={(event) => setDisplayName(event.target.value)}
					placeholder="Enter your name"
					leftIcon={<User className="h-4 w-4" />}
					required
				/>

				<AuthFormField
					label="Email"
					type="email"
					value={email}
					onChange={(event) => setEmail(event.target.value)}
					placeholder="Enter your email"
					leftIcon={<Mail className="h-4 w-4" />}
					required
				/>

				<AuthFormField
					label="Password"
					type="password"
					value={password}
					onChange={(event) => setPassword(event.target.value)}
					placeholder="Create a password"
					leftIcon={<Lock className="h-4 w-4" />}
					helperText="Must be at least 8 characters"
					required
				/>

				<div className="overflow-hidden rounded-2xl border border-border/50 bg-background/50 p-3">
					<TurnstilWidget onVerify={setCaptchaVerified} />
				</div>

				<div className="space-y-4">
					<label className="flex items-start gap-2 text-sm text-muted-foreground">
						<input type="checkbox" required className="mt-1 h-4 w-4 rounded border-border" />
						<span>
							I agree to the{" "}
							<a href="/contact" target="_blank" rel="noopener noreferrer" className="font-medium text-primary hover:opacity-80">
								Contact Us
							</a>{" "}
							and{" "}
							<a href="/privacy" target="_blank" rel="noopener noreferrer" className="font-medium text-primary hover:opacity-80">
								Privacy Policy
							</a>
						</span>
					</label>
				</div>

				<Button
					type="submit"
					className="h-12 w-full rounded-2xl text-base font-semibold shadow-lg transition-all duration-200 hover:scale-[1.01] active:scale-[0.98]"
					loading={isLoading}
					disabled={submitButtonDisabled}
					leftIcon={<UserPlus className="h-5 w-5" />}
				>
					{isLoading ? "Creating Account..." : "Create Account"}
				</Button>
			</motion.form>
		</AuthShell>
	);
};